import { useCallback, useEffect, useState } from "react";
import { useRevalidator } from "@remix-run/react";
import { Star } from "lucide-react";
import { useSupabaseContext } from "~/lib/supabase_context";
import { useStoreContext } from "~/hooks/use-store";
import Button from "~/components/theme/Button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "~/components/ui/card";
import { Label } from "~/components/ui/label";
import { Textarea } from "~/components/ui/textarea";

type Eligibility =
  | { status: "loading" }
  | { status: "guest" }
  | { status: "already_reviewed" }
  | { status: "not_eligible" }
  | { status: "ready" };

/** Public-facing label: `Jane D.` when both names exist; initials from first + last. */
function reviewerDisplayFromNames(
  firstName: string | null | undefined,
  lastName: string | null | undefined,
): { reviewer_display_name: string | null; reviewer_initials: string | null } {
  const first = firstName?.trim() ?? "";
  const last = lastName?.trim() ?? "";

  let reviewer_display_name: string | null = null;
  if (first && last) {
    reviewer_display_name = `${first} ${last.charAt(0).toUpperCase()}.`;
  } else if (first) {
    reviewer_display_name = first;
  } else if (last) {
    reviewer_display_name = `${last.charAt(0).toUpperCase()}.`;
  } 
  
  let reviewer_initials: string | null = null;
  if (first && last) {
    reviewer_initials = `${first.charAt(0)}${last.charAt(0)}`.toUpperCase();
  } else if (first.length >= 2) {
    reviewer_initials = first.slice(0, 2).toUpperCase();
  } else if (first) {
    reviewer_initials = first.charAt(0).toUpperCase();
  } else if (last) {
    reviewer_initials = last.charAt(0).toUpperCase();
  } 

  return { reviewer_display_name, reviewer_initials };
}

export default function StoreReviewComposer() {
  const { supabase } = useSupabaseContext();
  const { storeDetails } = useStoreContext();
  const revalidator = useRevalidator();

  const [eligibility, setEligibility] = useState<Eligibility>({
    status: "loading",
  });
  const [rating, setRating] = useState(5);
  const [comment, setComment] = useState("");
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const storeId = storeDetails.id;
  const storeName = (storeDetails.name ?? "").trim();

  const runEligibility = useCallback(async () => {
    const {
      data: { session },
    } = await supabase.auth.getSession();
    const userId = session?.user?.id;

    if (!userId) {
      setEligibility({ status: "guest" });
      return;
    }

    setEligibility({ status: "loading" });

    const [{ data: existing }, { data: canReview, error: rpcError }] =
      await Promise.all([
        supabase
          .from("StoreReviews")
          .select("id")
          .eq("store_id", storeId)
          .eq("user_id", userId)
          .maybeSingle(),
        supabase.rpc("user_has_purchased_from_store", {
          p_store_id: storeId,
        }),
      ]);

    if (rpcError) {
      console.error("user_has_purchased_from_store:", rpcError);
      setEligibility({ status: "not_eligible" });
      return;
    }

    if (existing) {
      setEligibility({ status: "already_reviewed" });
      return;
    }

    if (canReview === true) {
      setEligibility({ status: "ready" });
      return;
    }

    setEligibility({ status: "not_eligible" });
  }, [storeId, supabase]);

  useEffect(() => {
    void runEligibility();
  }, [runEligibility]);

  useEffect(() => {
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange(() => {
      void runEligibility();
    });
    return () => subscription.unsubscribe();
  }, [supabase, runEligibility]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (eligibility.status !== "ready") return;

    const {
      data: { session },
    } = await supabase.auth.getSession();
    const userId = session?.user?.id;
    if (!userId) return;

    setSubmitError(null);
    setSubmitting(true);

    const { data: profile } = await supabase
      .from("Users")
      .select("first_name, last_name")
      .eq("id", userId)
      .maybeSingle();

    const { reviewer_display_name, reviewer_initials } = reviewerDisplayFromNames(
      profile?.first_name,
      profile?.last_name,
    );

    const { error } = await supabase.from("StoreReviews").insert({
      store_id: storeId,
      user_id: userId,
      rating,
      comment: comment.trim() || null,
      reviewer_display_name,
      reviewer_initials,
    });

    setSubmitting(false);

    if (error) {
      setSubmitError(error.message);
      return;
    }

    setComment("");
    setEligibility({ status: "already_reviewed" });
    revalidator.revalidate();
  };

  if (eligibility.status === "loading") {
    return (
      <Card className="w-full rounded-2xl border border-dashed border-gray-200 bg-white/80 shadow-sm dark:border-gray-600 dark:bg-gray-900/30">
        <CardContent className="py-8 text-center text-sm text-muted-foreground">
          Checking whether you can review this store…
        </CardContent>
      </Card>
    );
  }

  if (eligibility.status === "guest") {
    return (
      <Card className="w-full rounded-2xl border border-gray-200/80 bg-white shadow-sm dark:border-gray-700 dark:bg-gray-900/40">
        <CardHeader>
          <CardTitle className="text-lg">Write a review</CardTitle>
          <CardDescription>
            Sign in to leave feedback for {storeName}.
          </CardDescription>
        </CardHeader>
      </Card>
    );
  }

  if (eligibility.status === "already_reviewed") {
    return (
      <Card className="w-full rounded-2xl border border-gray-200/80 bg-white shadow-sm dark:border-gray-700 dark:bg-gray-900/40">
        <CardContent className="py-6 text-sm text-muted-foreground">
          You have already submitted a review for this store. Thank you for
          your feedback.
        </CardContent>
      </Card>
    );
  }

  if (eligibility.status === "not_eligible") {
    return (
      <div className="w-full rounded-2xl border border-dashed border-gray-200 bg-white/80 px-6 py-5 text-left shadow-sm dark:border-gray-600 dark:bg-gray-900/30">
        <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
          Who can review?
        </p>
        <p className="mt-2 text-sm leading-relaxed text-gray-600 dark:text-gray-400">
          {`Only customers who have received a delivered order from ${storeName} can leave a store review.`}
        </p>
      </div>
    );
  }

  return (
    <Card className="w-full rounded-2xl border border-gray-200/80 bg-white shadow-sm dark:border-gray-700 dark:bg-gray-900/40">
      <CardHeader>
        <CardTitle className="text-lg">Write a store review</CardTitle>
        <CardDescription>
          {`Share your experience shopping with ${storeName}. You can submit one review per store.`}
        </CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label>Rating</Label>
            <div className="mt-2 flex gap-1">
              {[1, 2, 3, 4, 5].map((star) => (
                <button
                  key={star}
                  type="button"
                  onClick={() => setRating(star)}
                  className="rounded focus:outline-none focus-visible:ring-2 focus-visible:ring-orange-500"
                  aria-label={`${star} stars`}
                >
                  <Star
                    className={`h-8 w-8 ${
                      star <= rating
                        ? "fill-amber-400 text-amber-400"
                        : "text-gray-300"
                    }`}
                  />
                </button>
              ))}
            </div>
          </div>

          <div>
            <Label htmlFor="store-review-comment">Comments (optional)</Label>
            <Textarea
              id="store-review-comment"
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder="What went well? What could be better?"
              rows={4}
              className="mt-2 focus-visible:border-orange-300 focus-visible:ring-2 focus-visible:ring-orange-500/25 focus-visible:ring-offset-0"
              maxLength={2000}
            />
          </div>

          {submitError && (
            <p className="text-sm text-destructive">{submitError}</p>
          )}

          <Button type="submit" mode="primary" disabled={submitting}>
            {submitting ? "Submitting…" : "Submit review"}
          </Button>
        </form>
      </CardContent>
    </Card>
  );
}
